<template>
  <div
    :class="[
      'base-select',
      { 'base-select--has-leading': !!$scopedSlots.leading },
    ]"
  >
    <span v-if="$scopedSlots.leading" class="base-select__leading">
      <slot name="leading" />
    </span>
    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -- label is provided by the consumer via aria-label / aria-labelledby through $attrs -->
    <select
      v-bind="$attrs"
      :value="value"
      :disabled="disabled"
      class="base-select__field"
      v-on="{ ...$listeners, input: onInput }"
    >
      <slot />
    </select>
    <ChevronIcon class="base-select__chevron" />
  </div>
</template>

<script lang="ts">
import ChevronIcon from '@/components/icon_components/ChevronIcon.vue'
import { defineComponent, h, type PropType } from 'vue'

export type OptionVariant = 'default' | 'info' | 'success' | 'error'

// eslint-disable-next-line vue/one-component-per-file -- BaseOption is co-located by design: it is the typed counterpart that maps `variant` to the `data-variant` attribute BaseSelect's CSS relies on
export const BaseOption = defineComponent({
  name: 'BaseOption',
  inheritAttrs: false,
  props: {
    // No `default: undefined` here: for an `unknown` prop type Vue's
    // InferPropType resolves the prop to the *default's* type, so declaring one
    // collapses this prop to `undefined` and every value a typed caller passes
    // fails to assign. Omitting it leaves the prop optional at runtime just the
    // same — an absent prop is `undefined` either way.
    // eslint-disable-next-line vue/require-default-prop -- see above
    value: {
      type: [String, Number, Boolean, Object, Array] as PropType<unknown>,
      required: false,
    },
    variant: {
      type: String as PropType<OptionVariant>,
      default: 'default',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { slots, attrs }) {
    return () =>
      h(
        'option',
        {
          attrs: {
            ...attrs,
            disabled: props.disabled,
            'data-variant': props.variant,
          },
          domProps: { value: props.value },
        },
        slots.default?.()
      )
  },
})

// eslint-disable-next-line vue/one-component-per-file -- see note above on BaseOption
export default defineComponent({
  name: 'BaseSelect',
  components: { ChevronIcon },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number, Boolean] as PropType<
        string | number | boolean | undefined
      >,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    // `input` is hijacked so v-model receives the selected value rather than
    // the raw Event. All other native events (change, focus, blur, keydown, …)
    // pass through unchanged via v-on="$listeners".
    input: (value: string) => typeof value === 'string',
  },
  setup(_, { emit }) {
    const onInput = (event: Event) => {
      emit('input', (event.target as HTMLSelectElement).value)
    }
    return { onInput }
  },
})
</script>

<style lang="scss" scoped>
.base-select {
  position: relative;
  display: inline-block;
  width: 100%;

  &__field {
    width: 100%;
    height: 38px;
    padding: 0 $spacing-xs 0 $spacing-3xs;
    border-radius: $border-radius-m;
    border: 1px solid $border-color-default;
    background-color: $color-canvas;
    color: $color-text;
    cursor: pointer;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:has(option:checked[data-variant='info']) {
      color: $color-blue-60;
      background-color: $color-blue-10;

      &:hover:not(:disabled) {
        background-color: $color-blue-20;
      }
    }

    &:has(option:checked[data-variant='success']) {
      color: $color-green-60;
      background-color: $color-green-05;

      &:hover:not(:disabled) {
        background-color: $color-green-10;
      }
    }

    &:has(option:checked[data-variant='error']) {
      color: $color-red-60;
      background-color: $color-red-05;

      &:hover:not(:disabled) {
        background-color: $color-red-10;
      }
    }
  }

  &--has-leading &__field {
    padding-left: calc(#{$spacing-3xs} + #{$spacing-xs} + #{$spacing-4xs});
  }

  &__leading {
    position: absolute;
    left: $spacing-3xs;
    top: 50%;
    display: flex;
    width: $spacing-xs;
    height: $spacing-xs;
    pointer-events: none;
    transform: translateY(-50%);
  }

  &__chevron {
    width: $spacing-3xs;
    height: $spacing-3xs;
    color: $color-text-light;
    rotate: 90deg;
    position: absolute;
    right: $spacing-3xs;
    top: calc(50% - 5px); // Vertically center chevron
    pointer-events: none;
    transform: translateY(-50%);
  }
}
</style>
